'use client';

import Image from 'next/image';
import Link from 'next/link';
import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { getAddress, isAddress } from 'viem';
import {
  formatCgv,
  formatCompactCgv,
  formatCoverage,
  formatDecimalDisplay,
  formatPercentageRaw,
  humanize,
  isCardanoAddress,
  percentageRaw,
  shortAddress,
  statusLabel,
} from './lib/eligibility';
import type {
  AllocationPath,
  ChainKey,
  EligibilityDataset,
  EligibilityRecord,
  ResolutionStatus,
} from './lib/types';

type InjectedProvider = {
  request: (args: {
    method: string;
    params?: readonly unknown[] | object;
  }) => Promise<unknown>;
  on?: (event: string, handler: (accounts: string[]) => void) => void;
  removeListener?: (
    event: string,
    handler: (accounts: string[]) => void
  ) => void;
};

const getInjectedProvider = () =>
  window.ethereum as InjectedProvider | undefined;

const CHAIN_ORDER: ChainKey[] = ['ethereum', 'bnb', 'cardano'];
const ELIGIBLE_STATUS_TOTALS: ResolutionStatus[] = [
  'direct_holder',
  'resolved_protocol_beneficiary',
];

function ExternalIcon() {
  return (
    <svg viewBox='0 0 20 20' aria-hidden='true'>
      <path d='M7.5 4.5h-3v11h11v-3M10 4.5h5.5V10M15 5 8.5 11.5' />
    </svg>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      className='copy-button'
      type='button'
      onClick={() => void copy()}
      aria-label={`Copy ${label}`}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function ChainBadge({ chain }: { chain: ChainKey }) {
  const label = chain === 'ethereum' ? 'Ξ' : chain === 'bnb' ? 'BNB' : 'ADA';
  return <span className={`chain-badge chain-${chain}`}>{label}</span>;
}

function StatusBadge({ status }: { status: ResolutionStatus }) {
  return (
    <span className={`status-badge status-${status}`}>
      {statusLabel(status)}
    </span>
  );
}

function SourceLink({
  chain,
  address,
  baseUrl,
}: {
  chain: ChainKey;
  address: string;
  baseUrl: string;
}) {
  const explorer =
    chain === 'ethereum'
      ? 'Etherscan'
      : chain === 'bnb'
        ? 'BscScan'
        : 'Cardanoscan';
  return (
    <a
      className='address-link'
      href={`${baseUrl}${address}`}
      target='_blank'
      rel='noreferrer noopener'
    >
      <span>{shortAddress(address)}</span>
      <ExternalIcon />
      <span className='sr-only'>View source on {explorer}</span>
    </a>
  );
}

function pathBasis(path: AllocationPath): ReactNode {
  const gross = formatDecimalDisplay(
    path.grossClaimDisplay,
    path.grossClaimDecimals === 6 ? 6 : 8
  );
  const showCoverage = Number(path.coverageRatio) < 0.999999999;
  const direct = path.claimType === 'direct_balance';
  if (direct)
    return <span className='path-note'>Historical token balance</span>;
  return (
    <span className='path-note'>
      Gross claim {gross} {path.claimAsset}
      {showCoverage
        ? ` · ${formatCoverage(path.coverageRatio)} custody coverage`
        : ''}
    </span>
  );
}

function AllocationTable({
  chain,
  paths,
  explorerAddressUrl,
}: {
  chain: ChainKey;
  paths: AllocationPath[];
  explorerAddressUrl: string;
}) {
  if (paths.length === 0) {
    return <p className='empty-chain'>No allocation paths on this chain.</p>;
  }

  return (
    <div className='table-wrap'>
      <table className='allocation-table'>
        <thead>
          <tr>
            <th scope='col'>Position</th>
            <th scope='col'>Custody source</th>
            <th scope='col'>Resolution</th>
            <th scope='col' className='numeric'>
              Snapshot CGV
            </th>
          </tr>
        </thead>
        <tbody>
          {paths.map((path, index) => (
            <tr key={`${path.sourceHolder}-${path.claimType}-${index}`}>
              <td data-label='Position'>
                <strong>{humanize(path.claimType)}</strong>
                {pathBasis(path)}
              </td>
              <td data-label='Custody source'>
                <SourceLink
                  chain={chain}
                  address={path.sourceHolder}
                  baseUrl={explorerAddressUrl}
                />
                <span className='path-note'>
                  {humanize(path.sourceCategory)}
                </span>
              </td>
              <td data-label='Resolution'>
                <StatusBadge status={path.resolutionStatus} />
              </td>
              <td
                data-label='Snapshot CGV'
                className='numeric allocation-amount'
              >
                {formatCgv(path.attributedRaw)}
                <span className='path-note'>CGV</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultPanel({
  record,
  dataset,
  lookupAddress,
}: {
  record: EligibilityRecord;
  dataset: EligibilityDataset;
  lookupAddress: string;
}) {
  const eligible = BigInt(record.eligibleRaw) > BigInt(0);
  const unresolved = BigInt(record.unresolvedRaw) > BigInt(0);
  const lockedOnly = !eligible && BigInt(record.lockedRaw) > BigInt(0);
  const statusClass = eligible
    ? 'result-eligible'
    : unresolved
      ? 'result-review'
      : 'result-ineligible';
  const resultTitle = eligible
    ? 'Included in the snapshot'
    : unresolved
      ? 'Manual review required'
      : lockedOnly
        ? 'Locked balance — not eligible'
        : 'No eligible balance';

  return (
    <section className={`result-panel ${statusClass}`} aria-live='polite'>
      <div className='result-heading'>
        <div>
          <p className='section-kicker'>Eligibility result</p>
          <div className='result-title-line'>
            <span className='result-icon' aria-hidden='true'>
              {eligible ? '✓' : unresolved ? '!' : '×'}
            </span>
            <h2>{resultTitle}</h2>
          </div>
          <div className='result-address'>
            <span>{record.address}</span>
            <CopyButton value={record.address} label='result address' />
          </div>
          {lookupAddress.toLowerCase() !== record.address.toLowerCase() && (
            <p className='resolved-alias'>
              Cardano payment address consolidated to this stake account.
            </p>
          )}
        </div>
        <div className='result-total'>
          <span>Eligible snapshot balance</span>
          <strong>{formatCgv(record.eligibleRaw)}</strong>
          <em>CGV</em>
        </div>
      </div>

      {unresolved && (
        <div className='review-notice'>
          <span className='review-dot' />
          <p>
            This address also controls{' '}
            <strong>{formatCgv(record.unresolvedRaw)} CGV</strong> in contract
            or script custody that could not be mapped to on-chain claimants. It
            is shown for review and is not included in the eligible total.
          </p>
        </div>
      )}

      <div className='chain-result-grid'>
        {CHAIN_ORDER.map((chain) => {
          const metadata = dataset.chains[chain];
          const allocation = record.chains[chain];
          return (
            <article className='chain-result-card' key={chain}>
              <div className='chain-result-head'>
                <div>
                  <ChainBadge chain={chain} />
                  <div>
                    <h3>{metadata.label}</h3>
                    <a
                      href={metadata.explorerBlockUrl}
                      target='_blank'
                      rel='noreferrer noopener'
                    >
                      Block {metadata.blockNumber.toLocaleString('en-US')}{' '}
                      <ExternalIcon />
                    </a>
                  </div>
                </div>
                <span>
                  {allocation.paths.length}{' '}
                  {allocation.paths.length === 1 ? 'path' : 'paths'}
                </span>
              </div>
              <div className='chain-metrics'>
                <div>
                  <span>Eligible</span>
                  <strong>{formatCgv(allocation.eligibleRaw)} CGV</strong>
                </div>
                {(BigInt(allocation.unresolvedRaw) > BigInt(0) ||
                  BigInt(allocation.lockedRaw) > BigInt(0)) && (
                  <div>
                    <span>Not in eligible total</span>
                    <strong>
                      {formatCgv(
                        (
                          BigInt(allocation.unresolvedRaw) +
                          BigInt(allocation.lockedRaw)
                        ).toString()
                      )}{' '}
                      CGV
                    </strong>
                  </div>
                )}
              </div>
              <AllocationTable
                chain={chain}
                paths={allocation.paths}
                explorerAddressUrl={metadata.explorerAddressUrl}
              />
            </article>
          );
        })}
      </div>
    </section>
  );
}

function EmptyResult({ address }: { address: string }) {
  return (
    <section className='result-panel result-ineligible' aria-live='polite'>
      <div className='empty-result'>
        <span className='result-icon' aria-hidden='true'>
          ×
        </span>
        <p className='section-kicker'>Eligibility result</p>
        <h2>No CGV snapshot allocation found</h2>
        <p>
          <span>{shortAddress(address)}</span> has no direct or
          protocol-attributed CGV in the three-chain snapshot.
        </p>
      </div>
    </section>
  );
}

function eligibleFromStatusTotals(
  totals: Record<ResolutionStatus, string>
): string {
  return ELIGIBLE_STATUS_TOTALS.reduce(
    (sum, status) => sum + BigInt(totals[status]),
    BigInt(0)
  ).toString();
}

function normalizeSnapshotAddress(value: string): string | null {
  if (isAddress(value)) return getAddress(value);
  if (isCardanoAddress(value)) return value.toLowerCase();
  return null;
}

function recordForAddress(
  dataset: EligibilityDataset,
  address: string
): EligibilityRecord | null {
  const lookupKey = address.toLowerCase();
  const recordKey = dataset.aliases[lookupKey] ?? lookupKey;
  return dataset.records[recordKey] ?? null;
}

function OverallSnapshot({ dataset }: { dataset: EligibilityDataset }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const totals = dataset.totals.byResolutionStatusRaw;
  const eligibleRaw = eligibleFromStatusTotals(totals);
  const snapshotSupplyRaw = CHAIN_ORDER.reduce(
    (sum, chain) => sum + BigInt(dataset.chains[chain].supplyRaw),
    BigInt(0)
  ).toString();

  return (
    <section className='snapshot-overview'>
      <div className='overview-heading'>
        <div>
          <p className='section-kicker'>Overall snapshot</p>
          <h2>All-wallet CGV allocation</h2>
        </div>
        <p>
          No wallet selected. These totals include every direct holder,
          reconciled protocol beneficiary and retained custody address across
          all three networks.
        </p>
      </div>

      <article className='global-total-card'>
        <div className='global-total-head'>
          <div className='global-token-title'>
            <span className='cgv-token-mark'>
              <Image
                src='/cgv-snapshot/cgv.svg'
                alt=''
                width={56}
                height={56}
              />
            </span>
            <div>
              <span>Eligible across all chains</span>
              <h3 title={`${formatCgv(eligibleRaw)} CGV`}>
                {formatCgv(eligibleRaw)} <small>CGV</small>
              </h3>
            </div>
          </div>
          <button
            className='overview-detail-toggle'
            type='button'
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            {detailsOpen ? 'Hide chain details' : 'Show chain details'}
            <span aria-hidden='true'>{detailsOpen ? '−' : '+'}</span>
          </button>
        </div>

        <div className='global-resolution-grid'>
          <div>
            <span>Direct holders</span>
            <strong>{formatCgv(totals.direct_holder)}</strong>
            <small>CGV</small>
          </div>
          <div>
            <span>Protocol attributed</span>
            <strong>{formatCgv(totals.resolved_protocol_beneficiary)}</strong>
            <small>CGV</small>
          </div>
          <div className='metric-review'>
            <span>Unresolved custody</span>
            <strong>{formatCgv(totals.retained_or_unresolved_contract)}</strong>
            <small>CGV · review only</small>
          </div>
          <div className='metric-locked'>
            <span>Locked / unclaimable</span>
            <strong>{formatCgv(totals.locked_unclaimable)}</strong>
            <small>CGV · excluded</small>
          </div>
        </div>

        <div className='overall-chain-list'>
          {CHAIN_ORDER.map((chain) => {
            const metadata = dataset.chains[chain];
            const chainTotals = dataset.totals.byChain[chain];
            const chainEligibleRaw = eligibleFromStatusTotals(
              chainTotals.byResolutionStatusRaw
            );
            return (
              <div className='overall-chain-row' key={chain}>
                <span className='overall-chain-label'>
                  <ChainBadge chain={chain} />
                  <span>
                    <strong>{metadata.label}</strong>
                    <small>
                      Block {metadata.blockNumber.toLocaleString('en-US')}
                    </small>
                  </span>
                </span>
                <span className='overall-chain-amount'>
                  <small>Eligible</small>
                  <strong>{formatCgv(chainEligibleRaw)} CGV</strong>
                </span>
                <span className='overall-chain-amount supply'>
                  <small>Snapshot supply</small>
                  <strong>{formatCgv(metadata.supplyRaw)} CGV</strong>
                </span>
              </div>
            );
          })}
        </div>

        {detailsOpen && (
          <div className='overall-chain-details'>
            {CHAIN_ORDER.map((chain) => {
              const metadata = dataset.chains[chain];
              const chainTotals = dataset.totals.byChain[chain];
              const statusTotals = chainTotals.byResolutionStatusRaw;
              const chainEligibleRaw = eligibleFromStatusTotals(statusTotals);
              const eligiblePercent = percentageRaw(
                chainEligibleRaw,
                metadata.supplyRaw
              );
              return (
                <article className='overall-chain-card' key={`detail-${chain}`}>
                  <header>
                    <div>
                      <ChainBadge chain={chain} />
                      <span>
                        <strong>{metadata.label}</strong>
                        <a
                          href={metadata.explorerBlockUrl}
                          target='_blank'
                          rel='noreferrer noopener'
                        >
                          Block {metadata.blockNumber.toLocaleString('en-US')}{' '}
                          <ExternalIcon />
                        </a>
                      </span>
                    </div>
                    <span>
                      {formatPercentageRaw(
                        chainEligibleRaw,
                        metadata.supplyRaw
                      )}{' '}
                      eligible
                    </span>
                  </header>
                  <div
                    className='eligibility-track'
                    aria-label={`${eligiblePercent.toFixed(2)} percent eligible`}
                  >
                    <span
                      style={{ width: `${Math.min(eligiblePercent, 100)}%` }}
                    />
                  </div>
                  <dl>
                    <div>
                      <dt>Direct holders</dt>
                      <dd>{formatCgv(statusTotals.direct_holder)} CGV</dd>
                    </div>
                    <div>
                      <dt>Protocol attributed</dt>
                      <dd>
                        {formatCgv(statusTotals.resolved_protocol_beneficiary)}{' '}
                        CGV
                      </dd>
                    </div>
                    <div>
                      <dt>Unresolved custody</dt>
                      <dd>
                        {formatCgv(
                          statusTotals.retained_or_unresolved_contract
                        )}{' '}
                        CGV
                      </dd>
                    </div>
                    <div>
                      <dt>Locked / unclaimable</dt>
                      <dd>{formatCgv(statusTotals.locked_unclaimable)} CGV</dd>
                    </div>
                  </dl>
                  <div className='overall-chain-meta'>
                    <span>
                      {metadata.holderCount.toLocaleString('en-US')} original
                      holders
                    </span>
                    <span>
                      {chainTotals.beneficiaryAddresses.toLocaleString('en-US')}{' '}
                      indexed addresses
                    </span>
                    <span>
                      {chainTotals.allocationRows.toLocaleString('en-US')} paths
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </article>

      <div className='overview-stats'>
        <div>
          <strong>{formatCompactCgv(snapshotSupplyRaw)}</strong>
          <span>Total CGV snapshot supply</span>
        </div>
        <div>
          <strong>
            {dataset.totals.beneficiaryAddresses.toLocaleString('en-US')}
          </strong>
          <span>Beneficiary & custody addresses</span>
        </div>
        <div>
          <strong>
            {dataset.totals.allocationRows.toLocaleString('en-US')}
          </strong>
          <span>Auditable allocation paths</span>
        </div>
      </div>

      <div className='method-grid'>
        <article>
          <span>01</span>
          <h3>Direct balances</h3>
          <p>
            EVM wallets, Safes, and Cardano stake-controlled balances at the
            exact cutoff blocks.
          </p>
        </article>
        <article>
          <span>02</span>
          <h3>Protocol unwind</h3>
          <p>
            LPs, staking, rewards, launchpad, vesting, allocation, and
            concentrated-liquidity positions.
          </p>
        </article>
        <article>
          <span>03</span>
          <h3>Conservative exceptions</h3>
          <p>
            Unreconciled EVM contract and Cardano script custody is flagged for
            review and excluded.
          </p>
        </article>
      </div>
    </section>
  );
}

export function CgvSnapshotClient() {
  const [dataset, setDataset] = useState<EligibilityDataset | null>(null);
  const [dataError, setDataError] = useState('');
  const [input, setInput] = useState('');
  const [lookupAddress, setLookupAddress] = useState('');
  const [record, setRecord] = useState<EligibilityRecord | null>(null);
  const [hasLookup, setHasLookup] = useState(false);
  const [formError, setFormError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectedWallet, setConnectedWallet] = useState('');

  useEffect(() => {
    let active = true;
    fetch('/cgv-snapshot/data/cgv-eligibility.json')
      .then((response) => {
        if (!response.ok)
          throw new Error(`Snapshot data returned HTTP ${response.status}`);
        return response.json() as Promise<EligibilityDataset>;
      })
      .then((loaded) => {
        if (!active) return;
        setDataset(loaded);
        const queryAddress =
          new URLSearchParams(window.location.search).get('address') ?? '';
        const normalized = normalizeSnapshotAddress(queryAddress);
        if (normalized) {
          setInput(normalized);
          setLookupAddress(normalized);
          setRecord(recordForAddress(loaded, normalized));
          setHasLookup(true);
        }
      })
      .catch((error: unknown) => {
        if (active)
          setDataError(
            error instanceof Error
              ? error.message
              : 'Unable to load snapshot data'
          );
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const provider = getInjectedProvider();
    if (!provider) return;
    provider
      .request({ method: 'eth_accounts' })
      .then((accounts) => {
        if (!Array.isArray(accounts)) return;
        if (accounts[0] && isAddress(accounts[0]))
          setConnectedWallet(getAddress(accounts[0]));
      })
      .catch(() => undefined);

    const onAccountsChanged = (accounts: string[]) => {
      setConnectedWallet(
        accounts[0] && isAddress(accounts[0]) ? getAddress(accounts[0]) : ''
      );
    };
    provider.on?.('accountsChanged', onAccountsChanged);
    return () =>
      provider.removeListener?.('accountsChanged', onAccountsChanged);
  }, []);

  function runLookup(rawAddress: string) {
    if (!dataset) return;
    const candidate = rawAddress.trim();
    if (!candidate) {
      setFormError('');
      setInput('');
      setLookupAddress('');
      setRecord(null);
      setHasLookup(false);
      const url = new URL(window.location.href);
      url.searchParams.delete('address');
      window.history.replaceState({}, '', url);
      return;
    }
    const normalized = normalizeSnapshotAddress(candidate);
    if (!normalized) {
      setFormError('Enter a valid EVM or Cardano payment/stake address.');
      setHasLookup(false);
      return;
    }
    setFormError('');
    setInput(normalized);
    setLookupAddress(normalized);
    setRecord(recordForAddress(dataset, normalized));
    setHasLookup(true);
    const url = new URL(window.location.href);
    url.searchParams.set('address', normalized);
    window.history.replaceState({}, '', url);
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    runLookup(input);
  }

  async function connectWallet() {
    const provider = getInjectedProvider();
    if (!provider) {
      setFormError(
        'No browser wallet detected. Paste an address instead, or install an EVM wallet extension.'
      );
      return;
    }
    setConnecting(true);
    setFormError('');
    try {
      const accounts = (await provider.request({
        method: 'eth_requestAccounts',
      })) as string[];
      if (!accounts[0] || !isAddress(accounts[0]))
        throw new Error('The wallet did not return a valid account.');
      const normalized = getAddress(accounts[0]);
      setConnectedWallet(normalized);
      runLookup(normalized);
    } catch (error: unknown) {
      setFormError(
        error instanceof Error ? error.message : 'Wallet connection failed.'
      );
    } finally {
      setConnecting(false);
    }
  }

  return (
    <div className='cgv-snapshot'>
      <div className='app-shell'>
        <div className='ambient ambient-one' />
        <div className='ambient ambient-two' />
        <header className='site-header'>
          <div className='page-frame header-inner'>
            <Link
              className='brand'
              href='/cgv-snapshot'
              aria-label='Singularity Finance CGV snapshot home'
            >
              <Image
                className='brand-mark'
                src='/cgv-snapshot/singularity-finance-logo.svg'
                alt=''
                width={43}
                height={43}
                priority
              />
              <span className='brand-wordmark'>
                <strong>Singularity</strong>
                <strong>Finance</strong>
              </span>
            </Link>
            <span className='header-product'>CGV recovery snapshot</span>
            <span
              className='audit-link'
              title='Snapshot methodology is included with the source repository'
            >
              <span className='live-dot' /> Verified dataset
            </span>
          </div>
        </header>

        <main className='page-frame'>
          <section className='hero'>
            <div className='hero-copy'>
              <p className='eyebrow'>
                <span /> Singularity Finance · Recovery snapshot
              </p>
              <h1>
                Your CGV snapshot.
                <br />
                <em>Attributed on-chain.</em>
              </h1>
              <div className='hero-intro'>
                <p>
                  You can look up and review your direct holdings and reconciled
                  protocol positions immediately before the Ethereum exploit,
                  time-aligned across Ethereum, BNB Chain, and Cardano.
                </p>
                <p>
                  Eligible CGV allocations will be honored at the original
                  migration rate of 1 CGV = 0.14122 SFI.
                </p>
                <p>
                  The official SFI claim page will be announced once it’s ready.
                  No action is required at this time.
                </p>
              </div>
            </div>
            <div className='cutoff-card'>
              <span className='cutoff-label'>Snapshot cutoff</span>
              <strong>20 Sep 2026</strong>
              <span>04:34:47 UTC</span>
              <div className='cutoff-rule' />
              {dataset ? (
                CHAIN_ORDER.map((chain) => (
                  <a
                    key={chain}
                    href={dataset.chains[chain].explorerBlockUrl}
                    target='_blank'
                    rel='noreferrer noopener'
                  >
                    <ChainBadge chain={chain} />
                    <span>
                      <small>{dataset.chains[chain].label}</small>#
                      {dataset.chains[chain].blockNumber.toLocaleString(
                        'en-US'
                      )}
                    </span>
                    <ExternalIcon />
                  </a>
                ))
              ) : (
                <span className='loading-line'>Loading blocks…</span>
              )}
            </div>
          </section>

          <section className='lookup-card'>
            <div className='lookup-heading'>
              <span className='step-number'>01</span>
              <div>
                <h2>Find your allocation</h2>
                <p>
                  Enter an EVM address, Cardano payment address, or Cardano
                  stake account.
                </p>
              </div>
            </div>
            <form onSubmit={onSubmit}>
              <label htmlFor='wallet-address'>
                Wallet, stake, or custody address (optional)
              </label>
              <div className={`input-row${formError ? ' input-error' : ''}`}>
                <span className='input-prefix' aria-hidden='true'>
                  {/^(addr|stake)1/i.test(input.trim()) ? '₳' : '0x'}
                </span>
                <input
                  id='wallet-address'
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder='Leave empty for totals, or enter an EVM / Cardano address'
                  spellCheck={false}
                  autoComplete='off'
                  disabled={!dataset}
                />
                <button
                  className='button button-primary'
                  type='submit'
                  disabled={!dataset}
                >
                  {input.trim() ? 'Check eligibility' : 'View all'}{' '}
                  <span aria-hidden='true'>→</span>
                </button>
              </div>
              <div className='form-footer'>
                <button
                  className='wallet-button'
                  type='button'
                  onClick={() => void connectWallet()}
                  disabled={!dataset || connecting}
                >
                  <span className='wallet-glyph' aria-hidden='true' />
                  {connecting
                    ? 'Connecting…'
                    : connectedWallet
                      ? `Use ${shortAddress(connectedWallet)}`
                      : 'Connect wallet'}
                </button>
                <p>
                  Your address is checked locally against the published dataset.
                </p>
              </div>
              {formError && (
                <p className='form-error' role='alert'>
                  {formError}
                </p>
              )}
            </form>
          </section>

          {dataError && (
            <div className='notice notice-error' role='alert'>
              Could not load the snapshot dataset: {dataError}. Please try again
              later.
            </div>
          )}

          {dataset &&
            hasLookup &&
            (record ? (
              <ResultPanel
                record={record}
                dataset={dataset}
                lookupAddress={lookupAddress}
              />
            ) : (
              <EmptyResult address={lookupAddress} />
            ))}

          {dataset && !hasLookup && <OverallSnapshot dataset={dataset} />}

          <section className='method-note'>
            <div>
              <span className='info-mark'>i</span>
              <p>
                <strong>How eligibility is calculated</strong>
                Eligible totals include direct balances and reconciled protocol
                beneficiaries. Safe custody stays with the Safe address. Staking
                shortfalls use the actual custody distributed pro rata;
                unresolved contract Cardano payment-script custody, and
                permanently locked LP minimum liquidity are not included.
              </p>
            </div>
            <a
              href={`https://etherscan.io/tx/${dataset?.cutoff.exploitTransaction ?? '0x789396ec99221dd842cf47cbbcb4c0906510dcfe71cf041dba35ac6ab68c8116'}`}
              target='_blank'
              rel='noreferrer noopener'
            >
              View exploit boundary <ExternalIcon />
            </a>
          </section>
        </main>

        <footer>
          <div className='page-frame footer-inner'>
            <p>
              <Image
                src='/cgv-snapshot/singularity-finance-logo.svg'
                alt=''
                width={24}
                height={24}
              />{' '}
              Singularity Finance · CGV recovery snapshot
            </p>
            <p>
              Technical attribution dataset · Not a final legal determination of
              entitlement
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
